/**
 * Auth Letter Payer Config — upload-based, non-technical editor.
 *
 * Displays the primary org's payer list. Before each Firestore write (toggle,
 * upload, add-with-copy, or sync), {@link SaveToOrgsModal} asks which of the
 * Agentic Auth On File orgs should receive the same field patch. Config state
 * lives in {@link useAuthLetterPayerConfig}; this component only renders the
 * list, search, and modals.
 * @module AuthLetterPayerConfig
 */
import { useCallback, useMemo, useState } from "react";
import { AUTH_LETTER_PAYER_CONFIG_ORGS } from "../../../../constants/agenticAuthOnFileOrgTabs";
import { OrganizationType } from "../../../../enums/organizationTypes";
import "../agenticAuthOnFileConfiguration/agenticAuthOnFileConfiguration.scss";
import { isEnabled, payerSection } from "./authLetterConfigUtils";
import AddPayerModal from "./components/AddPayerModal";
import PayerConfigCard, { UploadedPrompt } from "./components/PayerConfigCard";
import SaveToOrgsModal, { SaveToOrgOption } from "./components/SaveToOrgsModal";
import { useAuthLetterPayerConfig } from "./useAuthLetterPayerConfig";

/** Pending write that waits on the save-to-orgs modal before running. */
type PendingSave =
  | {
      kind: "toggle";
      payer: string;
      enabled: boolean;
      summary: string;
    }
  | {
      kind: "upload";
      payer: string;
      parsed: UploadedPrompt;
      fileName: string;
      summary: string;
      resolve: (result: "saved" | "cancelled" | "failed") => void;
    }
  | {
      kind: "add";
      name: string;
      copyFrom: string;
      summary: string;
      resolve: (ok: boolean) => void;
    }
  | {
      kind: "sync";
      payer: string;
      summary: string;
    };

/** Delay after the OS file picker closes before showing the org modal. */
const UPLOAD_MODAL_OPEN_DELAY_MS = 180;

/** Orgs available in the save picker. */
const ORG_OPTIONS: SaveToOrgOption[] = AUTH_LETTER_PAYER_CONFIG_ORGS.map(
  (tab) => ({ id: tab.id, label: tab.label }),
);

const ORG_LABEL_BY_ID: Record<string, string> = Object.fromEntries(
  ORG_OPTIONS.map((o) => [o.id, o.label]),
);

/** Page component for the Auth Letter Payer Config editor. */
const AuthLetterPayerConfig = () => {
  // UI always loads from NYCBS; saves can fan out to the agentic-auth orgs.
  const orgId = OrganizationType.NYCBS;

  const {
    doc,
    payers,
    savedPayerKeys,
    loading,
    savingPayer,
    togglePayer,
    uploadPrompt,
    addPayer,
    syncPayerToOrgs,
  } = useAuthLetterPayerConfig(orgId, ORG_LABEL_BY_ID);

  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

  const visiblePayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? payers.filter((p) => p.toLowerCase().includes(q)) : payers;
  }, [payers, search]);

  /**
   * Closes the org picker and rejects any awaiting upload/add promise.
   */
  const cancelPendingSave = useCallback(() => {
    if (pendingSave?.kind === "upload") {
      pendingSave.resolve("cancelled");
    } else if (pendingSave?.kind === "add") {
      pendingSave.resolve(false);
    }
    setPendingSave(null);
  }, [pendingSave]);

  /**
   * Runs the queued mutation against the orgs chosen in the modal.
   *
   * @param targetOrgIds - Orgs selected in {@link SaveToOrgsModal}
   */
  const confirmPendingSave = useCallback(
    async (targetOrgIds: string[]) => {
      const pending = pendingSave;
      setPendingSave(null);
      if (!pending) return;

      if (pending.kind === "toggle") {
        await togglePayer(pending.payer, pending.enabled, targetOrgIds);
        return;
      }
      if (pending.kind === "upload") {
        const ok = await uploadPrompt(
          pending.payer,
          pending.parsed,
          targetOrgIds,
        );
        pending.resolve(ok ? "saved" : "failed");
        return;
      }
      if (pending.kind === "sync") {
        await syncPayerToOrgs(pending.payer, targetOrgIds);
        return;
      }
      const ok = await addPayer(pending.name, pending.copyFrom, targetOrgIds);
      pending.resolve(ok);
      if (ok) setShowAddModal(false);
    },
    [pendingSave, togglePayer, uploadPrompt, addPayer, syncPayerToOrgs],
  );

  /**
   * Opens the org picker before toggling a payer On/Off.
   *
   * @param payer - Payer being toggled
   * @param enabled - Desired enabled state
   */
  const handleToggle = (payer: string, enabled: boolean) => {
    setPendingSave({
      kind: "toggle",
      payer,
      enabled,
      summary: `Turn "${payer}" ${enabled ? "On" : "Off"} in the selected organizations.`,
    });
  };

  /**
   * Opens the org picker after a short delay so the OS file dialog can finish
   * closing before this modal animates in.
   *
   * @param payer - Payer receiving the upload
   * @param parsed - Parsed prompt JSON
   * @param fileName - Name of the chosen file (shown in the modal)
   * @return Promise that resolves when the modal finishes (save, cancel, or fail)
   */
  const handleUpload = (
    payer: string,
    parsed: UploadedPrompt,
    fileName: string,
  ): Promise<"saved" | "cancelled" | "failed"> =>
    new Promise((resolve) => {
      window.setTimeout(() => {
        setPendingSave({
          kind: "upload",
          payer,
          parsed,
          fileName,
          summary: `Choose where to save the prompt for "${payer}".`,
          resolve,
        });
      }, UPLOAD_MODAL_OPEN_DELAY_MS);
    });

  /**
   * Adds a draft locally, or opens the org picker when copying an existing prompt.
   *
   * @param name - New payer name
   * @param copyFrom - Existing payer to copy, or empty for a blank draft
   */
  const handleAddPayer = async (name: string, copyFrom: string) => {
    if (!copyFrom) {
      // Blank draft — no Firestore write yet, so no org picker.
      if (await addPayer(name, copyFrom, [orgId])) setShowAddModal(false);
      return;
    }
    await new Promise<boolean>((resolve) => {
      setPendingSave({
        kind: "add",
        name,
        copyFrom,
        summary: `Add "${name}" (copied from "${copyFrom}") to the selected organizations.`,
        resolve,
      });
    });
  };

  /**
   * Opens the org picker to copy an existing NYCBS payer prompt to other orgs.
   *
   * @param payer - Saved payer whose current prompt will be synced
   */
  const handleSyncToOrgs = (payer: string) => {
    setPendingSave({
      kind: "sync",
      payer,
      summary: `Copy the current NYCBS prompt for "${payer}" to the selected organizations.`,
    });
  };

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden">
      <div className="mb-2 flex items-center gap-2 text-h11 font-bold text-primaryGray-1">
        Auth Letter Payer Config
      </div>
      <div className="mt-1 rounded-md border border-primaryGray-14 bg-primaryGray-16 px-3 py-2 text-tiny leading-relaxed text-primaryGray-6">
        You’re viewing the{" "}
        <span className="font-semibold text-primaryGray-1">NYCBS</span> config.
        Each save lets you choose which orgs to update. Click the copy icon on a
        payer to copy that prompt to other orgs.
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          className="w-64 rounded border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
          placeholder="Search by payer name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          onClick={() => setShowAddModal(true)}
          className="rounded border border-gray-800 bg-gray-800 px-3 py-2 text-tiny font-semibold text-white hover:bg-gray-700"
        >
          + Add Payer
        </button>
        <span className="ml-auto text-sm text-primaryGray-6">
          {payers.length} payer{payers.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-2">
        {loading ? (
          <div className="py-10 text-center text-sm text-primaryGray-6">
            Loading auth letter payer config…
          </div>
        ) : visiblePayers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-primaryGray-14 py-10 text-center text-sm text-primaryGray-6">
            {payers.length === 0
              ? "No payers configured yet. Click “+ Add Payer” to create one."
              : "No payers match your search."}
          </div>
        ) : (
          visiblePayers.map((payer) => (
            <PayerConfigCard
              key={payer}
              name={payer}
              jcode={payerSection(doc, "j_code_info", payer)}
              provider={payerSection(doc, "prompt_provider_info", payer)}
              enabled={isEnabled(doc, payer)}
              unsaved={!savedPayerKeys.has(payer.toLowerCase())}
              saving={savingPayer === payer}
              onToggle={(enabled) => handleToggle(payer, enabled)}
              onUpload={(parsed, fileName) =>
                handleUpload(payer, parsed, fileName)
              }
              onSyncToOrgs={() => handleSyncToOrgs(payer)}
            />
          ))
        )}
      </div>

      {showAddModal && (
        <AddPayerModal
          existingPayers={payers}
          onCancel={() => setShowAddModal(false)}
          onCreate={handleAddPayer}
        />
      )}

      {pendingSave && (
        <SaveToOrgsModal
          summary={pendingSave.summary}
          fileName={
            pendingSave.kind === "upload" ? pendingSave.fileName : undefined
          }
          organizations={
            pendingSave.kind === "sync"
              ? ORG_OPTIONS.filter((o) => o.id !== orgId)
              : ORG_OPTIONS
          }
          initialSelectedOrgIds={pendingSave.kind === "sync" ? [] : [orgId]}
          onCancel={cancelPendingSave}
          onConfirm={confirmPendingSave}
        />
      )}
    </div>
  );
};

export default AuthLetterPayerConfig;
